import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Phone } from "lucide-react";
import { COMPANY } from "../lib/site";
import { Reveal } from "./Reveal";

export function useSeo(title, description) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      let tag = document.querySelector('meta[name="description"]');
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", "description");
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", description);
    }
  }, [title, description]);
}

export const Eyebrow = ({ children, className = "" }) => (
  <span className={`eyebrow inline-flex items-center gap-2 text-brand-red ${className}`}>
    <span className="h-2 w-2 rotate-45 bg-brand-red" aria-hidden="true" /> {children}
  </span>
);

export const SectionHeader = ({ eyebrow, title, body, dark = false, align = "left", className = "" }) => (
  <div className={`${align === "center" ? "mx-auto text-center" : ""} max-w-3xl ${className}`}>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <h2 className={`mt-4 text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl ${dark ? "text-white" : "text-brand-black"}`}>
      {title}
    </h2>
    {body && <p className={`mt-4 text-base leading-relaxed ${dark ? "text-white/70" : "text-brand-gray"}`}>{body}</p>}
  </div>
);

export const CTAButton = ({ to, href, children, variant = "primary", testId, external = false, icon = true }) => {
  const base = "group inline-flex items-center gap-2 px-6 py-3.5 text-sm font-bold transition-all duration-300";
  const styles = {
    primary: "bg-brand-red text-white hover:bg-brand-redhover",
    dark: "bg-brand-black text-white hover:bg-brand-charcoal",
    outline: "border border-brand-black/20 text-brand-black hover:border-brand-red hover:text-brand-red",
    outlineLight: "border border-white/25 text-white hover:border-white hover:bg-white/5",
  };
  const content = (
    <>
      {children}
      {icon && <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
    </>
  );
  if (href) {
    return (
      <a href={href} data-testid={testId} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined} className={`${base} ${styles[variant]}`}>
        {content}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testId} className={`${base} ${styles[variant]}`}>
      {content}
    </Link>
  );
};

// Reusable dark hero for service/subpages.
export const PageHero = ({ eyebrow, title, intro, image, imageAlt }) => (
  <section className="relative overflow-hidden bg-brand-black pt-32 pb-20 sm:pt-40 sm:pb-28">
    <div className="absolute inset-0 grid-overlay opacity-40" aria-hidden="true" />
    {image && (
      <div className="absolute inset-0" aria-hidden="true">
        <img src={image} alt="" className="h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-black via-brand-black/85 to-brand-black/40" />
      </div>
    )}
    <div className="relative mx-auto max-w-7xl px-5">
      <Reveal>
        <Eyebrow>{eyebrow}</Eyebrow>
      </Reveal>
      <motion.h1
        className="mt-5 max-w-4xl text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        {title}
      </motion.h1>
      {intro && (
        <Reveal delay={0.15}>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/70">{intro}</p>
        </Reveal>
      )}
    </div>
  </section>
);

export const FinalCTA = () => (
  <section className="bg-brand-red" data-testid="final-cta">
    <div className="mx-auto max-w-7xl px-5 py-20 sm:py-28">
      <div className="grid items-center gap-10 lg:grid-cols-2">
        <div>
          <Eyebrow className="text-white">Complete Brand Support</Eyebrow>
          <h2 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
            One Partner. Every Event.
          </h2>
          <p className="mt-5 max-w-lg text-lg leading-relaxed text-white/90">
            Tell us what you're managing, where it needs to go, and when it needs to arrive. MJG will help bring the pieces together.
          </p>
        </div>
        <div className="flex flex-col gap-4 sm:flex-row lg:justify-end">
          <Link to="/contact" data-testid="finalcta-start" className="group inline-flex items-center justify-center gap-2 bg-white px-7 py-4 text-sm font-bold text-brand-black transition-colors hover:bg-brand-black hover:text-white">
            Start a Project <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <a href={COMPANY.phoneHref} data-testid="finalcta-call" className="inline-flex items-center justify-center gap-2 border border-white/40 px-7 py-4 text-sm font-bold text-white transition-colors hover:bg-white/10">
            <Phone className="h-4 w-4" /> Call {COMPANY.phone}
          </a>
        </div>
      </div>
    </div>
  </section>
);
